import { useState } from 'react';
import { Link } from 'react-router-dom';
import Navbar from '../home/components/Navbar';
import Footer from '../home/components/Footer';

const WhatIsHoFH = () => {
  const [activeContentType, setActiveContentType] = useState<string | null>(null);

  const videoContent = [
    {
      title: 'HoFH Explained: The Severe Form of FH',
      duration: '10:45',
      views: '18K views',
      thumbnail:
        'https://public.readdy.ai/ai/img_res/0df20c0eeea4d07590ff031ecc926886.jpg',
    },
    {
      title: 'How HoFH Devastates Arteries From Childhood',
      duration: '12:10',
      views: '15K views',
      thumbnail:
        'https://readdy.ai/api/search-image?query=Medical%20illustration%20showing%20severely%20blocked%20artery%20cross%20section%20with%20massive%20cholesterol%20plaque%20buildup%20in%20young%20patient%20depicting%20homozygous%20familial%20hypercholesteremia%20damage%20educational%20healthcare%20visualization%20warm%20coral%20and%20teal%20tones%20clean%20background&width=400&height=250&seq=hofh-vid-thumb2&orientation=landscape',
    },
    {
      title: 'Advanced Therapies for HoFH Management',
      duration: '14:30',
      views: '21K views',
      thumbnail:
        'https://readdy.ai/api/search-image?query=Specialist%20doctor%20reviewing%20advanced%20treatment%20options%20for%20severe%20genetic%20cholesterol%20disorder%20including%20PCSK9%20inhibitors%20and%20lipoprotein%20apheresis%20on%20digital%20screen%20in%20bright%20modern%20cardiology%20clinic%20warm%20natural%20lighting%20professional%20healthcare%20setting&width=400&height=250&seq=hofh-vid-thumb3&orientation=landscape',
    },
  ];

  const handleContentTypeClick = (id: string) => {
    setActiveContentType(activeContentType === id ? null : id);
  };

  return (
    <div className="min-h-screen bg-white">
      <Navbar />

      {/* Hero Section */}
      <section className="pt-28 pb-16 bg-gradient-to-br from-[#fef9f9] via-[#fef5f5] to-[#faf5f8]">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center mb-12">
            <span className="inline-flex items-center gap-2 px-4 py-2 bg-white rounded-full text-sm text-gray-600 mb-6 shadow-sm">
              <i className="ri-heart-pulse-line text-[#f04e58]"></i>
              Familial Heart Disease Awareness
            </span>
            <h1 className="text-4xl lg:text-5xl font-bold text-gray-900 leading-tight mb-6">
              What Is Homozygous Familial<br />Hypercholesterolemia (HoFH)?
            </h1>
            <p className="text-gray-600 text-lg leading-relaxed max-w-3xl mx-auto">
              Homozygous Familial Hypercholesterolemia (HoFH) is the most severe form of inherited high cholesterol, occurring when a child inherits a faulty gene from <strong>both</strong> parents. Affecting roughly 1 in 250,000 to 1 in 360,000 people, HoFH can cause life-threatening cardiovascular disease in childhood if left untreated.
            </p>
          </div>

          {/* Content Type Cards */}
          <div className="grid grid-cols-3 gap-6">
            {/* Blog Card */}
            <div
              onClick={() => handleContentTypeClick('blog')}
              className={`relative bg-white rounded-2xl overflow-hidden cursor-pointer transition-all duration-300 hover:shadow-lg group ${
                activeContentType === 'blog'
                  ? 'ring-2 ring-[#f04e58] shadow-lg'
                  : 'shadow-sm hover:shadow-md'
              }`}
            >
              <div className="w-full h-48">
                <img
                  src="https://readdy.ai/api/search-image?query=Close%20up%20of%20a%20doctor%20holding%20a%20glowing%20red%20anatomical%20heart%20model%20with%20DNA%20strands%20and%20cholesterol%20molecules%20floating%20around%20it%20representing%20severe%20homozygous%20familial%20hypercholesterolemia%20genetic%20heart%20disease%20dark%20moody%20medical%20photography%20warm%20coral%20accent%20lighting%20clean%20background&width=400&height=300&seq=hofh-blog-card-001&orientation=landscape"
                  alt="Blog articles about HoFH"
                  className="w-full h-full object-cover object-top"
                />
              </div>
              <div className="p-6">
                <div
                  className={`w-12 h-12 rounded-xl flex items-center justify-center mb-4 transition-colors ${
                    activeContentType === 'blog' ? 'bg-[#f04e58]' : 'bg-[#fef5f5] group-hover:bg-[#f04e58]'
                  }`}
                >
                  <i
                    className={`ri-article-line text-xl transition-colors ${
                      activeContentType === 'blog' ? 'text-white' : 'text-[#f04e58] group-hover:text-white'
                    }`}
                  ></i>
                </div>
                <h3 className="font-semibold text-gray-900 text-lg mb-2">Blog</h3>
                <p className="text-gray-500 text-sm">In-depth guide to understanding HoFH</p>
                <div
                  className={`absolute top-4 right-4 w-8 h-8 rounded-full flex items-center justify-center transition-all ${
                    activeContentType === 'blog' ? 'bg-[#f04e58] rotate-45' : 'bg-white/90 backdrop-blur-sm'
                  }`}
                >
                  <i
                    className={`ri-add-line text-lg ${
                      activeContentType === 'blog' ? 'text-white' : 'text-gray-700'
                    }`}
                  ></i>
                </div>
              </div>
            </div>

            {/* Video Card */}
            <div
              onClick={() => handleContentTypeClick('videos')}
              className={`relative bg-white rounded-2xl overflow-hidden cursor-pointer transition-all duration-300 hover:shadow-lg group ${
                activeContentType === 'videos'
                  ? 'ring-2 ring-[#f04e58] shadow-lg'
                  : 'shadow-sm hover:shadow-md'
              }`}
            >
              <div className="w-full h-48">
                <img
                  src="https://readdy.ai/api/search-image?query=Medical%20professional%20presenting%20educational%20content%20about%20severe%20inherited%20cholesterol%20disorder%20homozygous%20familial%20hypercholesterolemia%20on%20large%20screen%20showing%20artery%20blockage%20animation%20in%20modern%20lecture%20hall%20warm%20professional%20lighting%20healthcare%20education%20setting&width=400&height=300&seq=hofh-video-card-001&orientation=landscape"
                  alt="Educational videos about HoFH"
                  className="w-full h-full object-cover object-top"
                />
              </div>
              <div className="p-6">
                <div
                  className={`w-12 h-12 rounded-xl flex items-center justify-center mb-4 transition-colors ${
                    activeContentType === 'videos' ? 'bg-[#f04e58]' : 'bg-[#fef5f5] group-hover:bg-[#f04e58]'
                  }`}
                >
                  <i
                    className={`ri-video-line text-xl transition-colors ${
                      activeContentType === 'videos' ? 'text-white' : 'text-[#f04e58] group-hover:text-white'
                    }`}
                  ></i>
                </div>
                <h3 className="font-semibold text-gray-900 text-lg mb-2">Video</h3>
                <p className="text-gray-500 text-sm">Visual explainers on HoFH basics</p>
                <div
                  className={`absolute top-4 right-4 w-8 h-8 rounded-full flex items-center justify-center transition-all ${
                    activeContentType === 'videos' ? 'bg-[#f04e58] rotate-45' : 'bg-white/90 backdrop-blur-sm'
                  }`}
                >
                  <i
                    className={`ri-add-line text-lg ${
                      activeContentType === 'videos' ? 'text-white' : 'text-gray-700'
                    }`}
                  ></i>
                </div>
              </div>
            </div>

            {/* Infograph Card */}
            <div
              onClick={() => handleContentTypeClick('infograph')}
              className={`relative bg-white rounded-2xl overflow-hidden cursor-pointer transition-all duration-300 hover:shadow-lg group ${
                activeContentType === 'infograph'
                  ? 'ring-2 ring-[#f04e58] shadow-lg'
                  : 'shadow-sm hover:shadow-md'
              }`}
            >
              <div className="w-full h-48">
                <img
                  src="https://readdy.ai/api/search-image?query=Detailed%20medical%20infographic%20poster%20about%20homozygous%20familial%20hypercholesterolemia%20HoFH%20showing%20both%20parents%20passing%20faulty%20genes%20to%20child%20with%20severely%20blocked%20arteries%20diagram%20and%20emergency%20treatment%20pathways%20modern%20healthcare%20data%20visualization%20coral%20teal%20and%20cream%20color%20palette%20professional%20medical%20illustration&width=400&height=300&seq=hofh-infograph-card-001&orientation=landscape"
                  alt="Homozygous Familial Hypercholesterolemia (HoFH) infographics"
                  className="w-full h-full object-cover object-top"
                />
              </div>
              <div className="p-6">
                <div
                  className={`w-12 h-12 rounded-xl flex items-center justify-center mb-4 transition-colors ${
                    activeContentType === 'infograph' ? 'bg-[#f04e58]' : 'bg-[#fef5f5] group-hover:bg-[#f04e58]'
                  }`}
                >
                  <i
                    className={`ri-bar-chart-box-line text-xl transition-colors ${
                      activeContentType === 'infograph' ? 'text-white' : 'text-[#f04e58] group-hover:text-white'
                    }`}
                  ></i>
                </div>
                <h3 className="font-semibold text-gray-900 text-lg mb-2">Infograph</h3>
                <p className="text-gray-500 text-sm">Homozygous Familial Hypercholesterolemia (HoFH)</p>
                <div
                  className={`absolute top-4 right-4 w-8 h-8 rounded-full flex items-center justify-center transition-all ${
                    activeContentType === 'infograph' ? 'bg-[#f04e58] rotate-45' : 'bg-white/90 backdrop-blur-sm'
                  }`}
                >
                  <i
                    className={`ri-add-line text-lg ${
                      activeContentType === 'infograph' ? 'text-white' : 'text-gray-700'
                    }`}
                  ></i>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Blog Content Section */}
      {activeContentType === 'blog' && (
        <section className="py-20 bg-white">
          <div className="max-w-4xl mx-auto px-6">
            {/* Article Header */}
            <div className="mb-12">
              <h2 className="text-4xl font-bold text-gray-900 mb-6 leading-tight">
                Beyond the Numbers: 5 Surprising Realities of HoFH
              </h2>
              <div className="flex items-center gap-4 text-sm text-gray-500 mb-8">
                <span className="flex items-center gap-2">
                  <i className="ri-calendar-line"></i>
                  Feb 8, 2025
                </span>
                <span className="flex items-center gap-2">
                  <i className="ri-time-line"></i>
                  9 min read
                </span>
              </div>
              <div className="w-full h-[400px] rounded-2xl overflow-hidden mb-8">
                <img
                  src="https://readdy.ai/api/search-image?query=Dramatic%20medical%20illustration%20of%20a%20human%20heart%20with%20severely%20narrowed%20coronary%20arteries%20showing%20extreme%20cholesterol%20plaque%20buildup%20representing%20homozygous%20familial%20hypercholesterolemia%20with%20DNA%20double%20helix%20in%20background%20warm%20coral%20and%20deep%20red%20tones%20professional%20scientific%20visualization%20dark%20moody%20atmosphere&width=800&height=400&seq=hofh-blog-hero-001&orientation=landscape"
                  alt="Homozygous Familial Hypercholesterolemia and heart disease"
                  className="w-full h-full object-cover object-top"
                />
              </div>
            </div>

            {/* Article Content */}
            <article className="prose prose-lg max-w-none">
              {/* Introduction */}
              <div className="mb-12">
                <div className="flex items-start gap-4 mb-4">
                  <div className="w-12 h-12 flex items-center justify-center rounded-xl bg-[#0d9488] text-white flex-shrink-0">
                    <i className="ri-lightbulb-line text-xl"></i>
                  </div>
                  <h3 className="text-2xl font-bold text-gray-900 mt-1">
                    Introduction: A Hidden Legacy
                  </h3>
                </div>
                <p className="text-gray-700 leading-relaxed mb-4 text-lg">
                  Most people associate high cholesterol with a lifetime of poor dietary choices or a sedentary lifestyle that catches up with them in middle age. However, for some, the struggle with "bad" cholesterol begins before they even take their first breath. Homozygous Familial Hypercholesterolemia (FoFH)—commonly referred to as HoFH—is a rare, severe, and inherited condition that fundamentally alters how the body manages fats. Unlike standard high cholesterol, HoFH is a genetic legacy that prevents the liver from filtering dangerous cholesterol from the blood, creating a life-threatening environment from the moment of birth. Understanding the unique realities of this disorder is the first step toward transforming a hidden risk into a legacy of protection.
                </p>
              </div>

              {/* Takeaway 1 */}
              <div className="mb-12 bg-[#fef9f9] rounded-2xl p-8">
                <div className="flex items-start gap-4 mb-4">
                  <div className="w-12 h-12 flex items-center justify-center rounded-xl bg-[#f04e58] text-white font-bold text-xl flex-shrink-0">
                    1
                  </div>
                  <h3 className="text-2xl font-bold text-gray-900 mt-1">
                    Takeaway 1: The Genetic Double-Whammy
                  </h3>
                </div>
                <p className="text-gray-700 leading-relaxed mb-4">
                  The cholesterol levels seen in HoFH patients are not just "high"—they are extreme. To understand the staggering biological stress this places on a child&apos;s circulatory system, one must look at the numbers:
                </p>
                <div className="bg-white rounded-xl p-6 mb-4">
                  <div className="space-y-4">
                    <div className="flex items-center justify-between">
                      <span className="text-gray-700 font-medium">Normal LDL Levels:</span>
                      <span className="text-[#0d9488] font-bold text-lg">Less than 130 mg/dL</span>
                    </div>
                    <div className="h-px bg-gray-200"></div>
                    <div className="flex items-center justify-between">
                      <span className="text-gray-700 font-medium">HoFH LDL Levels:</span>
                      <span className="text-[#f04e58] font-bold text-lg">Greater than 500–700+ mg/dL</span>
                    </div>
                  </div>
                </div>
                <p className="text-gray-700 leading-relaxed">
                  These levels are 4 to 6 times higher than what is considered healthy. Because these extreme concentrations of LDL are present from birth, the blood vessels are under constant, intense pressure, leading to the rapid accumulation of plaque in the aorta and coronary arteries long before a child reaches adolescence.
                </p>
              </div>

              {/* Takeaway 2 */}
              <div className="mb-12 bg-[#fef9f9] rounded-2xl p-8">
                <div className="flex items-start gap-4 mb-4">
                  <div className="w-12 h-12 flex items-center justify-center rounded-xl bg-[#f04e58] text-white font-bold text-xl flex-shrink-0">
                    2
                  </div>
                  <h3 className="text-2xl font-bold text-gray-900 mt-1">
                    Takeaway 2: When the Skin Tells a Story
                  </h3>
                </div>
                <p className="text-gray-700 leading-relaxed mb-4">
                  Because HoFH involves such massive amounts of circulating cholesterol, the body eventually begins to store the excess in visible ways. These fatty skin deposits are known as Xanthomas.
                </p>
                <p className="text-gray-700 leading-relaxed mb-4">
                  They typically emerge between the ages of 3 and 5. For parents, recognizing these marks is more than a matter of medical literacy—it is a life-saving skill. As an advocate for these children, I urge parents to view these visible signs as a vital "early warning system." Recognizing a Xanthoma can be the first step in uncovering the invisible, life-threatening hardening of the arteries occurring inside a child&apos;s body, allowing for intervention before a cardiac event occurs.
                </p>
              </div>

              {/* Takeaway 3 */}
              <div className="mb-12 bg-[#fef9f9] rounded-2xl p-8">
                <div className="flex items-start gap-4 mb-4">
                  <div className="w-12 h-12 flex items-center justify-center rounded-xl bg-[#f04e58] text-white font-bold text-xl flex-shrink-0">
                    3
                  </div>
                  <h3 className="text-2xl font-bold text-gray-900 mt-1">
                    Takeaway 3: High-Tech Intervention: Filtering Blood Like Dialysis
                  </h3>
                </div>
                <p className="text-gray-700 leading-relaxed mb-4">
                  Managing HoFH requires a level of intervention that goes far beyond traditional "diet and exercise." While a heart-healthy lifestyle is a non-negotiable foundation, it cannot overcome the genetic absence of LDL receptors.
                </p>
                <p className="text-gray-700 leading-relaxed mb-4">
                  Modern science has introduced Lipoprotein Apheresis, a specialized, dialysis-like procedure. In this treatment, the patient&apos;s blood is filtered every 1–2 weeks to physically remove LDL cholesterol directly from the bloodstream. This intensive regimen is paired with a aggressive combination of advanced medications:
                </p>
                <div className="bg-white rounded-xl p-6 mb-4">
                  <div className="grid md:grid-cols-2 gap-3">
                    <div className="flex items-center gap-3">
                      <div className="w-2 h-2 rounded-full bg-[#0d9488]"></div>
                      <span className="text-gray-700">Statins</span>
                    </div>
                    <div className="flex items-center gap-3">
                      <div className="w-2 h-2 rounded-full bg-[#0d9488]"></div>
                      <span className="text-gray-700">Ezetimibe</span>
                    </div>
                    <div className="flex items-center gap-3">
                      <div className="w-2 h-2 rounded-full bg-[#0d9488]"></div>
                      <span className="text-gray-700">PCSK9 inhibitors</span>
                    </div>
                    <div className="flex items-center gap-3">
                      <div className="w-2 h-2 rounded-full bg-[#0d9488]"></div>
                      <span className="text-gray-700">Lomitapide</span>
                    </div>
                    <div className="flex items-center gap-3">
                      <div className="w-2 h-2 rounded-full bg-[#0d9488]"></div>
                      <span className="text-gray-700">Evinacumab</span>
                    </div>
                  </div>
                </div>
                <p className="text-gray-700 leading-relaxed">
                  In addition to these high-tech therapies, strict adherence to a heart-healthy diet, regular exercise, and avoiding smoking are critical to managing the condition. By combining these approaches, medical professionals can achieve a 60–80% reduction in LDL levels, a feat that would be impossible through lifestyle changes alone.
                </p>
              </div>

              {/* Takeaway 4 */}
              <div className="mb-12 bg-[#fef9f9] rounded-2xl p-8">
                <div className="flex items-start gap-4 mb-4">
                  <div className="w-12 h-12 flex items-center justify-center rounded-xl bg-[#f04e58] text-white font-bold text-xl flex-shrink-0">
                    4
                  </div>
                  <h3 className="text-2xl font-bold text-gray-900 mt-1">
                    Takeaway 4: The Accelerated Timeline of Heart Disease
                  </h3>
                </div>
                <p className="text-gray-700 leading-relaxed mb-4">
                  In the general population, heart disease is a condition of old age. However, in the context of HoFH, the "heart age" of a patient is drastically disconnected from their chronological age. Atherosclerosis—the narrowing and hardening of the arteries—begins in infancy.
                </p>
                <p className="text-gray-700 leading-relaxed mb-4">
                  This accelerated timeline means that the medical milestones usually reserved for the elderly occur in early childhood. It is counter-intuitive and harrowing to imagine a primary school student facing the cardiovascular health of an 80-year-old, yet without aggressive intervention, this is the reality of the condition.
                </p>
                <div className="bg-gradient-to-r from-[#f04e58] to-[#0d9488] rounded-xl p-6 text-white">
                  <p className="text-xl font-bold text-white text-center leading-relaxed">
                    &quot;Without early treatment, heart disease can begin in early childhood, with some children requiring heart surgery or bypass by age 7–10.&quot;
                  </p>
                </div>
              </div>

              {/* Takeaway 5 */}
              <div className="mb-12 bg-[#fef9f9] rounded-2xl p-8">
                <div className="flex items-start gap-4 mb-4">
                  <div className="w-12 h-12 flex items-center justify-center rounded-xl bg-[#f04e58] text-white font-bold text-xl flex-shrink-0">
                    5
                  </div>
                  <h3 className="text-2xl font-bold text-gray-900 mt-1">
                    Takeaway 5: When the Skin Tells a Story
                  </h3>
                </div>
                <p className="text-gray-700 leading-relaxed mb-4">
                  Because HoFH involves such massive amounts of circulating cholesterol, the body eventually begins to store the excess in visible ways. These fatty skin deposits are known as Xanthomas.
                </p>
                <p className="text-gray-700 leading-relaxed mb-4">
                  They typically emerge between the ages of 3 and 5. For parents, recognizing these marks is more than a matter of medical literacy—it is a life-saving skill. As an advocate for these children, I urge parents to view these visible signs as a vital "early warning system." Recognizing a Xanthoma can be the first step in uncovering the invisible, life-threatening hardening of the arteries occurring inside a child&apos;s body, allowing for intervention before a cardiac event occurs.
                </p>
              </div>

              {/* Conclusion */}
              <div className="mb-12 bg-gradient-to-br from-[#fef9f9] to-[#faf5f8] rounded-2xl p-8 border-2 border-[#0d9488]/20">
                <div className="flex items-start gap-4 mb-4">
                  <div className="w-12 h-12 flex items-center justify-center rounded-xl bg-[#0d9488] text-white flex-shrink-0">
                    <i className="ri-lightbulb-flash-line text-xl"></i>
                  </div>
                  <h3 className="text-2xl font-bold text-gray-900 mt-1">
                    Conclusion: A Future Redefined
                  </h3>
                </div>
                <p className="text-gray-700 leading-relaxed mb-4">
                  While HoFH is a severe and life-threatening diagnosis, it is no longer a hopeless one. Aggressive, early treatment can lower LDL levels so significantly that the onset of heart disease is delayed or even prevented, allowing patients to live full, vibrant lives into adulthood.
                </p>
                <p className="text-gray-700 leading-relaxed">
                  The key to changing the narrative of HoFH lies in early detection. Because this condition is a family affair, early genetic testing for the entire family is the most powerful tool we have. By identifying at-risk individuals early, we can replace a "hidden legacy" of risk with a "legacy of protection" for future generations. Through medical literacy and advanced science, we can ensure that a child&apos;s first breath is the start of a long and healthy journey.
                </p>
              </div>

              {/* Call to Action */}
              <div className="mt-12 bg-gradient-to-r from-[#f04e58] to-[#0d9488] rounded-2xl p-8 text-white">
                <h3 className="text-2xl font-bold mb-4">Early Detection Can Save Lives</h3>
                <p className="text-white/90 leading-relaxed mb-6">
                  If there is a history of very high cholesterol or early heart disease in your family, don&apos;t wait. HoFH is treatable when caught early. Schedule a consultation to discuss genetic screening for you and your family.
                </p>
                <Link
                  to="/consultation"
                  className="inline-flex items-center px-6 py-3 bg-white text-[#f04e58] rounded-full font-semibold hover:bg-gray-100 transition-all cursor-pointer whitespace-nowrap"
                >
                  Schedule Your Screening
                  <i className="ri-arrow-right-line ml-2"></i>
                </Link>
              </div>
            </article>
          </div>
        </section>
      )}

      {/* Video Content Section */}
      {activeContentType === 'videos' && (
        <section className="py-20 bg-white">
          <div className="max-w-4xl mx-auto px-6">
            {/* Video Header */}
            <div className="mb-8">
              <h2 className="text-3xl font-bold text-gray-900 mb-4">
                What Is HoFH? A Visual Explanation
              </h2>
              <p className="text-gray-600 leading-relaxed">
                Watch this educational video to understand how Homozygous Familial Hypercholesterolemia differs from HeFH, why it&apos;s so dangerous, and what treatment options are available for affected individuals and families.
              </p>
            </div>

            {/* Video Player */}
            <div className="relative w-full rounded-2xl overflow-hidden shadow-xl bg-gray-900 aspect-video mb-8">
              <video
                className="w-full h-full object-contain"
                controls
                autoPlay
                playsInline
                poster="https://readdy.ai/api/search-image?query=Medical%20educational%20video%20thumbnail%20about%20homozygous%20familial%20hypercholesterolemia%20HoFH%20showing%20heart%20and%20cholesterol%20molecules%20with%20play%20button%20overlay%20dark%20cinematic%20background%20warm%20coral%20and%20teal%20accent%20colors%20professional%20healthcare%20content&width=800&height=450&seq=hofh-video-poster-001&orientation=landscape"
              >
                <source
                  src="https://rr4---sn-nx57ynsk.googlevideo.com/videoplayback?expire=1771408798&ei=fnGVafymL_TVn88Pt5a36QU&ip=151.186.183.87&id=a8a3e3833e23933d&itag=22&source=contrib_service_notebooklm&begin=0&requiressl=yes&xpc=EghoqJzIP3oBAQ==&met=1771401598,&mh=tl&mm=32&mn=sn-nx57ynsk&ms=su&mv=m&mvi=4&pl=26&rms=su,su&sc=yes&susc=nblm&app=fife&ic=1045&eaua=vlflJW24Q14&pcm2=yes&mime=video/mp4&vprv=1&rqh=1&dur=325.683&lmt=1771400089684853&mt=1771400687&txp=0011224&sparams=expire,ei,ip,id,itag,source,requiressl,xpc,susc,app,ic,eaua,pcm2,mime,vprv,rqh,dur,lmt&sig=AJEij0EwRgIhANpr7gGKu3IeFk1A723xE3XC05q5hmvDJYMCcewQQh-OAiEA6OfAp7DuVjuGmQUX9vENRL1Gw4x9aYw6jWd2lfhYEB0=&lsparams=met,mh,mm,mn,ms,mv,mvi,pl,rms,sc&lsig=APaTxxMwRAIgcNffpsoLmN5OkXT4DMa_SWv2qHJFQ0moy2TfVuWrsTYCIEq1UPa-PxvB7LWKjtGVesXgfiWHaE-9yKpsIuBqZbmM"
                  type="video/mp4"
                />
                Your browser does not support the video tag.
              </video>
            </div>

            {/* Video Info */}
            <div className="bg-[#fef9f9] rounded-2xl p-6">
              <div className="flex items-center gap-4 mb-4">
                <div className="w-12 h-12 rounded-full bg-[#f04e58] flex items-center justify-center">
                  <i className="ri-play-fill text-white text-xl"></i>
                </div>
                <div>
                  <h3 className="font-semibold text-gray-900">Understanding HoFH Series</h3>
                  <p className="text-sm text-gray-500">Educational Content</p>
                </div>
              </div>
              <p className="text-gray-700 leading-relaxed">
                This series covers the genetic basis of HoFH, how it differs from HeFH, the inheritance pattern requiring both parents to carry the gene, the dramatic physical signs, available treatment options including lipoprotein apheresis and newer therapies, and the critical importance of early diagnosis and cascade screening.
              </p>
            </div>

            {/* Related Videos */}
            <div className="mt-12">
              <h3 className="text-xl font-semibold text-gray-900 mb-6">Related Educational Videos</h3>
              <div className="grid md:grid-cols-3 gap-6">
                {videoContent.map((video, index) => (
                  <div
                    key={index}
                    className="bg-white rounded-xl overflow-hidden shadow-md hover:shadow-lg transition-shadow cursor-pointer group"
                  >
                    <div className="relative h-40 overflow-hidden">
                      <img
                        src={video.thumbnail}
                        alt={video.title}
                        className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-300"
                      />
                      <div className="absolute inset-0 bg-black/30 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity">
                        <div className="w-12 h-12 rounded-full bg-white/90 flex items-center justify-center">
                          <i className="ri-play-fill text-[#f04e58] text-2xl"></i>
                        </div>
                      </div>
                      <div className="absolute bottom-2 right-2 bg-black/70 text-white text-xs px-2 py-1 rounded">
                        {video.duration}
                      </div>
                    </div>
                    <div className="p-4">
                      <h4 className="font-medium text-gray-900 text-sm mb-1 group-hover:text-[#f04e58] transition-colors">
                        {video.title}
                      </h4>
                      <p className="text-xs text-gray-500">{video.views}</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </section>
      )}

      {/* Infograph Content Section */}
      {activeContentType === 'infograph' && (
        <section className="py-20 bg-white">
          <div className="max-w-5xl mx-auto px-6">
            {/* Infograph Header */}
            <div className="mb-8 text-center">
              <h2 className="text-3xl font-bold text-gray-900 mb-4">
                Understanding HoFH: A Visual Guide
              </h2>
              <p className="text-gray-600 leading-relaxed max-w-3xl mx-auto">
                A comprehensive visual overview of Homozygous Familial Hypercholesterolemia—from genetic causes and extreme cholesterol levels to advanced treatment options and hope for the future.
              </p>
            </div>

            {/* Main Infographic */}
            <div className="bg-[#fef9f9] rounded-2xl p-6 shadow-lg mb-12">
              <div className="w-full rounded-xl overflow-hidden">
                <img
                  src="https://static.readdy.ai/image/ff0b7e80ce034da2f0ffbed7fce4b50d/64cf1256c7776683b6ff366710e5da67.png"
                  alt="Understanding HoFH: A Guide to Homozygous Familial Hypercholesterolemia - Infographic showing the impact of extreme cholesterol and aggressive treatment options"
                  className="w-full h-auto object-contain object-center"
                />
              </div>
            </div>

            {/* Key Highlights */}
            <div className="grid md:grid-cols-3 gap-6">
              <div className="bg-white rounded-xl p-6 border border-gray-100 shadow-sm hover:shadow-md transition-shadow">
                <div className="w-12 h-12 flex items-center justify-center rounded-xl bg-[#f04e58]/10 mb-4">
                  <i className="ri-dna-line text-xl text-[#f04e58]"></i>
                </div>
                <h3 className="font-semibold text-gray-900 mb-2">Double Gene Defect</h3>
                <p className="text-sm text-gray-600 leading-relaxed">
                  HoFH occurs when both parents pass on a faulty cholesterol gene, resulting in LDL levels 4-6x higher than normal from birth.
                </p>
              </div>

              <div className="bg-white rounded-xl p-6 border border-gray-100 shadow-sm hover:shadow-md transition-shadow">
                <div className="w-12 h-12 flex items-center justify-center rounded-xl bg-[#0d9488]/10 mb-4">
                  <i className="ri-heart-pulse-line text-xl text-[#0d9488]"></i>
                </div>
                <h3 className="font-semibold text-gray-900 mb-2">Childhood Heart Disease</h3>
                <p className="text-sm text-gray-600 leading-relaxed">
                  Without treatment, atherosclerosis begins in infancy, with some children requiring heart surgery by age 7-10.
                </p>
              </div>

              <div className="bg-white rounded-xl p-6 border border-gray-100 shadow-sm hover:shadow-md transition-shadow">
                <div className="w-12 h-12 flex items-center justify-center rounded-xl bg-[#f04e58]/10 mb-4">
                  <i className="ri-syringe-line text-xl text-[#f04e58]"></i>
                </div>
                <h3 className="font-semibold text-gray-900 mb-2">Advanced Therapies</h3>
                <p className="text-sm text-gray-600 leading-relaxed">
                  Lipoprotein Apheresis combined with specialized medications can reduce LDL by 60-80%, enabling full, healthy lives.
                </p>
              </div>
            </div>

            {/* Share Options */}
            <div className="mt-10 flex flex-wrap justify-center gap-4">
              <button className="inline-flex items-center px-6 py-3 bg-[#f04e58] text-white rounded-full font-medium hover:bg-[#d93d47] transition-all cursor-pointer whitespace-nowrap">
                <i className="ri-download-line mr-2"></i>
                Download Infographic
              </button>
              <button className="inline-flex items-center px-6 py-3 border border-gray-300 text-gray-700 rounded-full font-medium hover:bg-gray-50 transition-all cursor-pointer whitespace-nowrap">
                <i className="ri-share-line mr-2"></i>
                Share
              </button>
            </div>
          </div>
        </section>
      )}

      <Footer />
    </div>
  );
};

export default WhatIsHoFH;
